/**
 * Legal content for the Gen Pro Hub /terms and /privacy pages.
 *
 * This is ecosystem-level copy (covers the whole Hub and every product behind
 * the single account), NOT the QR-Generator-specific text that lives in the
 * `qr` app. Keep both pages in sync via the shared LegalPage shell.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️  NOTE: This copy is solid boilerplate, not legal advice. The responsible
 * party and jurisdiction are set in COMPANY below (individual operator,
 * Poland). A legal review is still recommended before/after launch. If a
 * registered business (JDG/NIP) or postal contact address becomes available,
 * add it so the data-controller identity is complete for GDPR.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const COMPANY = {
  /** Public product / brand name. */
  productName: "Gen Pro Hub",
  /**
   * Responsible party / data controller. Currently an individual operator.
   * If a registered business is set up (JDG + NIP), replace with its details.
   */
  legalEntity: "Dmytro Fedchyshyn",
  /** Country where the controller is based; also the governing-law country. */
  controllerCountry: "Poland",
  /** Where the Service is hosted (EU) — used for the transfers disclosure. */
  hostingLocation: "Frankfurt, Germany",
  governingLaw: "Poland",
  courts: "the competent courts of Poland",
  /** Contact for both general and privacy/GDPR matters. */
  contactEmail: "support@gen-pro-hub.com",
  /** Human-readable "last updated" shown at the top of each page. */
  lastUpdated: "July 2026",
  /** Products reachable from the single Hub account. */
  products:
    "QR Generator, Planner, Invoice, and other tools we may offer from time to time",
} as const;

export type LegalSection = {
  title: string;
  /** One or more paragraphs. */
  body?: string[];
  /** Optional bullet list rendered under the paragraphs. */
  bullets?: string[];
};

// ═══════════════════════════════════════════════════════════════════════════
// TERMS OF SERVICE
// ═══════════════════════════════════════════════════════════════════════════

export const TERMS_SECTIONS: LegalSection[] = [
  {
    title: "1. Acceptance of Terms",
    body: [
      `By creating an account, accessing, or using ${COMPANY.productName} (the "Service") — including any of the tools reachable from your account, such as ${COMPANY.products} — you agree to be bound by these Terms of Service. If you are using the Service on behalf of an organisation, you confirm that you are authorised to accept these Terms for that organisation. If you do not agree, do not use the Service.`,
    ],
  },
  {
    title: "2. The Service",
    body: [
      `${COMPANY.productName} is a single account that gives you access to a suite of self-serve business tools. Each tool may have its own features, plans, and limits, which are described where the tool is offered. The Service is provided by ${COMPANY.legalEntity}, based in ${COMPANY.controllerCountry}, and is hosted in the European Union (${COMPANY.hostingLocation}).`,
      `We are continually improving the Service. We may add, change, or remove features, and we may launch tools as beta or early-access, which may be less stable and may change or be withdrawn.`,
    ],
  },
  {
    title: "3. Accounts and Eligibility",
    body: [
      `You need an account to use most of the Service. You are responsible for the information you provide, for keeping your credentials secure, and for all activity that happens under your account. You must be at least 16 years old, or the age of digital consent in your country, to use the Service.`,
      `Notify us promptly at ${COMPANY.contactEmail} if you believe your account has been accessed without authorisation.`,
    ],
  },
  {
    title: "4. Plans, Billing, and Payments",
    body: [
      `Some tools and features are offered on paid plans. Prices, billing cycles, and included limits are shown at the point of purchase. Payments are processed by our payment provider, Stripe; we do not store full card details.`,
      `Paid plans renew automatically for the same period unless cancelled before the renewal date. Unless required by law or stated otherwise at purchase, payments are non-refundable for the period already started. Prices are exclusive of taxes unless stated, and applicable taxes will be added. We may change prices with reasonable advance notice; changes take effect at your next renewal.`,
    ],
  },
  {
    title: "5. Acceptable Use",
    body: [
      `You agree not to misuse the Service. In particular, you must not:`,
    ],
    bullets: [
      "use the Service for any unlawful, fraudulent, or deceptive purpose, or to infringe anyone's rights;",
      "upload or distribute malware, or link content (including QR codes and other generated assets) to malicious, illegal, or harmful destinations;",
      "attempt to gain unauthorised access to the Service, other accounts, or our systems, or to disrupt or overload the Service;",
      "reverse-engineer, resell, or offer the Service as your own product, except as expressly permitted; or",
      "use the Service to harass others or to send unsolicited or unlawful communications.",
    ],
  },
  {
    title: "6. Your Content and Intellectual Property",
    body: [
      `You keep ownership of the content and data you provide, and of the assets you generate with the Service (for example, the QR codes, documents, and designs you create). You grant us the limited rights needed to host, process, and display that content so we can operate and provide the Service to you.`,
      `The Service itself — including its software, design, and branding — remains our property or that of our licensors. We grant you a limited, non-exclusive, non-transferable right to use the Service in accordance with these Terms. You are responsible for ensuring you have the rights to any content you upload.`,
    ],
  },
  {
    title: "7. Third-Party Services",
    body: [
      `The Service relies on third-party providers, including Stripe for payments and Amazon Web Services (including AWS Cognito) for authentication and hosting. Your use of features powered by these providers may also be subject to their terms. We are not responsible for third-party services we do not control, including any destination a QR code or link points to.`,
    ],
  },
  {
    title: "8. Availability, Changes, and Beta Features",
    body: [
      `We aim to keep the Service available and reliable, but we do not guarantee uninterrupted or error-free operation. We may perform maintenance, and we may modify or discontinue parts of the Service, giving reasonable notice of material changes where practical. Beta and early-access features are provided "as is" and may be changed or removed at any time.`,
    ],
  },
  {
    title: "9. Disclaimers and Limitation of Liability",
    body: [
      `To the fullest extent permitted by law, the Service is provided "as is" and "as available" without warranties of any kind. We are not liable for indirect, incidental, special, or consequential damages, or for loss of profits, data, or business. Nothing in these Terms limits liability that cannot be limited by law (such as for death or personal injury caused by negligence, or for fraud).`,
      `Subject to the above, our total aggregate liability arising out of or related to the Service is limited to the amount you paid us for the Service in the twelve months before the event giving rise to the claim.`,
    ],
  },
  {
    title: "10. Termination",
    body: [
      `You may stop using the Service and close your account at any time. We may suspend or terminate your access if you breach these Terms, if required by law, or for legitimate operational reasons, giving notice where reasonable. On termination, your right to use the Service ends; provisions that by their nature should survive (such as intellectual property, disclaimers, and limitation of liability) will continue to apply.`,
    ],
  },
  {
    title: "11. Governing Law and Disputes",
    body: [
      `These Terms are governed by the laws of ${COMPANY.governingLaw}, without regard to its conflict-of-laws rules, and subject to any mandatory consumer-protection rights you have in your country of residence. You agree to the exclusive jurisdiction of ${COMPANY.courts}, except where applicable law gives you the right to bring proceedings elsewhere.`,
    ],
  },
  {
    title: "12. Changes to These Terms",
    body: [
      `We may update these Terms from time to time. We will post the revised version and update the "Last updated" date above. For material changes we will provide reasonable notice, for example by email or an in-app notice. Continuing to use the Service after changes take effect means you accept the updated Terms.`,
    ],
  },
  {
    title: "13. Contact",
    body: [
      `Questions about these Terms? Contact us at ${COMPANY.contactEmail}.`,
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// PRIVACY POLICY
// ═══════════════════════════════════════════════════════════════════════════

export const PRIVACY_SECTIONS: LegalSection[] = [
  {
    title: "1. Who We Are",
    body: [
      `${COMPANY.legalEntity}, based in ${COMPANY.controllerCountry} ("we", "us"), operates ${COMPANY.productName} and is the data controller for personal data processed through the Service. This policy explains what we collect, why, and the rights you have. For any privacy question you can reach us at ${COMPANY.contactEmail}.`,
    ],
  },
  {
    title: "2. Information We Collect",
    body: [`We collect:`],
    bullets: [
      "information you provide — such as your name, email address, and account settings;",
      "content and usage data — the tools you use, the assets you create (for example QR codes, plans, and invoices), and how you interact with the Service;",
      "technical data — such as IP address, device and browser type, and log data; and",
      "payment data — handled by Stripe. We receive limited billing details (such as plan and status) but do not store full card numbers.",
    ],
  },
  {
    title: "3. How and Why We Use Information",
    body: [
      `We use your information to provide and secure the Service, process payments, provide support, comply with legal obligations, and improve our products. Under the GDPR our legal bases are: performance of our contract with you (to provide the Service), our legitimate interests (such as securing and improving the Service), your consent (where we ask for it, for example for certain analytics or marketing), and compliance with legal obligations. We may use aggregated or anonymised data for analytics.`,
    ],
  },
  {
    title: "4. Sharing and Processors",
    body: [
      `We do not sell your personal data. We share it only with service providers who process it on our behalf under appropriate agreements, including Stripe (payments), Amazon Web Services and AWS Cognito (hosting and authentication), and providers that help us operate and support the Service. We may also disclose data where required by law or to protect our rights and users.`,
    ],
  },
  {
    title: "5. International Transfers",
    body: [
      `The Service is hosted in the European Union (${COMPANY.hostingLocation}). Where data is transferred to or processed by providers outside the EU/EEA, we rely on appropriate safeguards such as the European Commission's Standard Contractual Clauses.`,
    ],
  },
  {
    title: "6. Data Retention",
    body: [
      `We keep personal data for as long as your account is active and as needed to provide the Service, then for as long as necessary to meet legal, accounting, or operational requirements. You can ask us to delete your account and associated data, subject to retention we are legally required to keep.`,
    ],
  },
  {
    title: "7. Security",
    body: [
      `We use technical and organisational measures appropriate to the risk to protect your data, including encryption in transit and access controls. No method of transmission or storage is completely secure, but we work to protect your information and to respond promptly to any incident.`,
    ],
  },
  {
    title: "8. Cookies",
    body: [
      `We use cookies and similar technologies for essential functions (such as keeping you signed in), for preferences (such as language), and, where you consent, for analytics. You can control cookies through your browser settings; disabling essential cookies may prevent parts of the Service from working.`,
    ],
  },
  {
    title: "9. Your Rights",
    body: [
      `Depending on your location, you have rights over your personal data. For users in the EU/EEA these include the rights to access, rectify, erase, restrict, and port your data, and to object to certain processing. Where we rely on consent, you can withdraw it at any time. You also have the right to lodge a complaint with your local data protection authority. To exercise your rights, contact us at ${COMPANY.contactEmail}.`,
    ],
  },
  {
    title: "10. Children",
    body: [
      `The Service is not directed to children. We do not knowingly collect personal data from anyone under the age of 16 (or the age of digital consent in your country). If you believe a child has provided us with personal data, contact us and we will delete it.`,
    ],
  },
  {
    title: "11. Changes to This Policy",
    body: [
      `We may update this policy from time to time. We will post the revised version and update the "Last updated" date above, and for material changes we will provide reasonable notice.`,
    ],
  },
  {
    title: "12. Contact",
    body: [
      `For privacy questions or to exercise your rights, contact us at ${COMPANY.contactEmail}.`,
    ],
  },
];
